import HoldingDetailsFlyout from '@/components/flyouts/HoldingDetailsFlyout';
import { buildHoldingAdvisorContext } from '@/components/flyouts/HoldingDetailsFlyout/holdingAdvisorContext';
import { BALANCE_DESC_GRID_SORT_MODEL } from '@/constants/grid';
import { GRID_COLUMN_WIDTH_NARROW } from '@/constants/dimensions';
import { PAGE_SIZE_OPTIONS, PAGE_SIZES } from '@/constants/pagination';
import { GRID_KEYS } from '@/constants/gridKeys';
import { DEBOUNCE_MS } from '@/constants/timing';
import { useAuthIsLoaded } from '@/hooks/useAuthIsLoaded';
import { useAccountDetailsFlyout } from '@/hooks/useAccountDetailsFlyout';
import { useGridColumns } from '@/hooks/useGridColumns';
import { useManagedColumnVisibility } from '@/hooks/useManagedColumnVisibility';
import { useServerBackedQuickFilter } from '@/hooks/useServerBackedQuickFilter';
import { ManagedColumnsManagement } from '@/components/table/ManagedColumnsManagement';
import { NoHoldingsRowsOverlay } from '@/components/table/NoHoldingsRowsOverlay';
import { StandardWidgetProps } from '@/models/WidgetModels';
import { RecordResponse } from '@/models/ServiceTypes';
import { useGetAccountsQuery } from '@/store/accountsApi';
import {
  HoldingsQueryParams,
  HoldingViewModel,
  useGetHoldingsQuery,
} from '@/store/holdingsApi';
import { formatNumberToCurrency } from '@/utils/currency';
import { formatAppDate, parseOptionalDate } from '@/utils/date';
import {
  formatUnrealizedGainsForExport,
  getNullsLastSortComparator,
  getUnrealizedGains,
  hasUsableCostBasis,
  unrealizedGainsAggregation,
} from './holdingsGainLoss';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Typography, useMediaQuery } from '@mui/material';
import {
  DataGridPremium,
  GRID_AGGREGATION_FUNCTIONS,
  GridColDef,
  GridGroupNode,
  GridListViewColDef,
  GridAggregationFunction,
  GridColumnVisibilityModel,
  GridAggregationModel,
  GridRenderCellParams,
  GridRowId,
  gridRowNodeSelector,
  useGridApiRef,
  useKeepGroupedColumnsHidden,
} from '@mui/x-data-grid-premium';
import { defaultToolbarExportSlotProps } from '@/components/table/toolbarSlotProps';
import { skipToken } from '@reduxjs/toolkit/query';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocalization } from '@/hooks/useLocalization';
import { useAutoResizeGroupingColumn } from '@/hooks/useAutoResizeGroupingColumn';
import CustomToolbarForNative from '@/components/CustomToolbarForNative';
import XsPreferencesDrawerToolbar from '@/components/table/XsPreferencesDrawerToolbar';
import { Capacitor } from '@capacitor/core';
import { useTheme } from '@mui/material/styles';
import { useTickerUrlFormatter } from '@/hooks/useTickerUrlFormatter';
import { useWidgetWidthIsBelow } from '@/hooks/useWidgetWidthIsBelow';
import type { GridPaginationModel } from '@mui/x-data-grid-premium';
import {
  GridContainer,
  type GridContainerSize,
} from '../../../components/grids/GridContainer';

export interface HoldingsTableProps extends StandardWidgetProps {
  endDate: string;
  accountId?: number;
  groupBy?: string;
  onDataLoaded?: () => void;
  investorId?: number;
  onlyPrimaryInvestors?: boolean;
  gridHeight?: number;
  size?: GridContainerSize;
  /** Advisor-only: the client's display name, threaded from ClientDetailsFlyout's Holdings tab. Omitted for the end-user page. */
  advisorClientName?: string;
  /** Advisor-only: whether the firm's "Show CUSIPs" AGSetting is enabled, already resolved by ClientContent.tsx. Omitted for the end-user page. */
  showCusip?: boolean;
}

type HoldingRow = HoldingViewModel & { id: number; CustomGroup?: string };

// HoldingViewModel optimistically types many fields as non-null, but the holdings
// API returns null for them per row. Reading values through this null-honest view
// keeps the runtime guards below type-correct (and not flagged as redundant)
// without weakening the shared model.
type NullableHoldingRow = { [K in keyof HoldingRow]: HoldingRow[K] | null };

// Placeholders shown by currency cells when the underlying value is null.
type CurrencyCellPlaceholder = '' | 'N/A' | '—';

const HOLDINGS_ROLLUP_FIELDS = [
  'Balance',
  'CostBasis',
  'UnrealizedGains',
  'UnrealizedGainsPercent',
  'EstimatedIncome',
] as const;

const HOLDINGS_COL_WIDTH_COMPACT_MOBILE = 80;
const HOLDINGS_COL_WIDTH_COMPACT_DESKTOP = 120;
const HOLDINGS_COL_WIDTH_CURRENCY_MOBILE = 120;
const HOLDINGS_COL_WIDTH_CURRENCY_NARROW_MOBILE = 100;
const HOLDINGS_COL_WIDTH_GAIN_LOSS_DESKTOP = 170;
const HOLDINGS_COL_WIDTH_PERCENT_MOBILE = 90;
const HOLDINGS_COL_WIDTH_CLASSIFICATIONS_DESKTOP = 200;
const HOLDINGS_COL_WIDTH_YIELD = 130;
const HOLDINGS_COL_WIDTH_SYMBOL = 100;
const HOLDINGS_COL_WIDTH_LAST_UPDATED_MOBILE = 100;
const HOLDINGS_COL_WIDTH_LAST_UPDATED_DESKTOP = 140;

// When grouping is active the grid aggregates client-side, so it must hold the
// entire dataset rather than a single server page — otherwise group rollups only
// reflect the loaded page and change as the user paginates. Request all rows in
// that case. The holdings endpoint applies `Limit` literally with no server-side
// cap (see SortAndLimitUtility), so an oversized limit safely returns the full set.
const ALL_HOLDINGS_LIMIT = 1_000_000;

const HOLDINGS_ROLLUP_AGGREGATION_FUNCTIONS: Partial<
  Record<(typeof HOLDINGS_ROLLUP_FIELDS)[number], string>
> = {
  UnrealizedGains: 'holdingsUnrealizedGains',
  UnrealizedGainsPercent: 'holdingsGainLossPercent',
};

const HOLDINGS_ROLLUP_AGGREGATION_MODEL =
  HOLDINGS_ROLLUP_FIELDS.reduce<GridAggregationModel>((model, field) => {
    model[field] = HOLDINGS_ROLLUP_AGGREGATION_FUNCTIONS[field] ?? 'sum';
    return model;
  }, {});

const createHoldingsGainLossPercentAggregation = (
  t: (key: string) => string,
): GridAggregationFunction<
  {
    balance?: number;
    costBasis?: number;
  },
  number | null
> => ({
  getCellValue: (params) => {
    const row = params.row as HoldingRow;
    return { balance: row.Balance, costBasis: row.CostBasis };
  },
  apply: ({ values }) => {
    let balance = 0;
    let costBasis = 0;

    values.forEach((value) => {
      const holding = { Balance: value?.balance, CostBasis: value?.costBasis };
      if (!hasUsableCostBasis(holding)) return;
      balance += holding.Balance;
      costBasis += holding.CostBasis;
    });

    if (costBasis === 0) return null;
    return ((balance - costBasis) / costBasis) * 100;
  },
  label: t('holdingsTable.percentChange'),
  columnTypes: ['number'],
});

// Normalizes a data field key for safe comparisons (trim + lowercase)
const normalizeFieldKey = (s: string) => (s || '').trim().toLowerCase();

const MOBILE_LIST_VIEW_OPTIONAL_FIELDS = [
  'CostBasis',
  'UnrealizedGains',
  'UnrealizedGainsPercent',
  'Classifications',
  'YieldToMarket',
  'CurrentYield',
  'YieldToMaturity',
  'EstimatedIncome',
  'MaturityDate',
  'InterestRate',
  'AnnualInterestAmount',
  'Symbol',
] as const;

const HoldingsTable: React.FC<HoldingsTableProps> = ({
  endDate,
  isWidget: _isWidget = false,
  accountId,
  groupBy = 'none',
  onDataLoaded,
  excludedAccounts,
  excludedAccountTypes,
  excludedCategories,
  investorId,
  onlyPrimaryInvestors,
  gridHeight,
  advisorClientName,
  showCusip,
  size,
}: HoldingsTableProps) => {
  const [isFlyoutOpen, setIsFlyoutOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<HoldingViewModel | null>(
    null,
  );
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: PAGE_SIZES.DEFAULT,
  });
  const authLoaded = useAuthIsLoaded();
  const apiRef = useGridApiRef();
  const { t } = useLocalization();

  const {
    openAccountById,
    openAccountByName,
    canOpenAccount,
    canOpenAccountByName,
    accountFlyout,
    getAccountById,
  } = useAccountDetailsFlyout({ investorId, onlyPrimaryInvestors });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const { ref: widgetWidthRef, isBelow: isWidgetBelowXsWidth } =
    useWidgetWidthIsBelow(_isWidget, theme.breakpoints.values.sm);
  const shouldUseListView = isXs || (_isWidget && isWidgetBelowXsWidth);
  const isNative = Capacitor.getPlatform() !== 'web';
  const forcedHiddenFields = useMemo(() => {
    if (groupBy === 'account') {
      return ['AccountName'];
    }
    if (groupBy !== 'none') {
      return ['CustomGroup'];
    }
    return [];
  }, [groupBy]);

  const defaultHiddenFieldsBase = useMemo(
    () =>
      isMobile
        ? ['CustomGroup', ...MOBILE_LIST_VIEW_OPTIONAL_FIELDS]
        : ['AccountName', 'CustomGroup'],
    [isMobile],
  );

  const { data: gridColumnsData, isLoading: gridColumnsLoading } =
    useGridColumns(GRID_KEYS.INVESTOR_HOLDINGS);

  const apiDefaultHiddenFields = useMemo(() => {
    const records = gridColumnsData?.Records ?? [];
    if (!records.length) return [] as string[];
    const disabled = records
      .filter((r) => !r.IsEnabled)
      .map((r) => (r.GridColumnDataField || '').trim())
      // Exclude fields we intentionally keep as static (UpdatedDate vs "lastupdated")
      .filter((f) => normalizeFieldKey(f) !== 'lastupdated');
    const seen = new Set<string>();
    const result: string[] = [];
    for (const f of disabled) {
      const key = normalizeFieldKey(f);
      if (!seen.has(key)) {
        seen.add(key);
        result.push(f);
      }
    }
    return result;
  }, [gridColumnsData]);

  // Combine base defaults (layout-driven) with API-provided defaults.
  const combinedDefaultHiddenFields = useMemo(
    () => [...defaultHiddenFieldsBase, ...apiDefaultHiddenFields],
    [defaultHiddenFieldsBase, apiDefaultHiddenFields],
  );

  const {
    columnVisibilityModel,
    handleColumnVisibilityChange,
    applyColumnVisibility,
    defaultColumnVisibilityModel,
  } = useManagedColumnVisibility<HoldingRow>({
    hideDisabledColumnMenu: true,
    defaultHiddenFields: combinedDefaultHiddenFields,
    forcedHiddenFields,
  });

  /**
   * Quick filter state for a search that runs over the whole book.
   *
   * Holdings are paged by the server, so a grid-side filter only ever sees the
   * page already fetched and silently hides matches on later pages. The term
   * goes to the endpoint instead, which is also what the legacy grid did.
   *
   * The grid is then set to `filterMode='server'`: left on the default it would
   * re-run the term over the rows it was handed, using only the columns this
   * grid renders, and drop server matches whose only hit was on a field that is
   * not a column here. That also makes the per-column Filters panel a no-op,
   * since it filters client-side, so it is removed rather than left looking
   * functional.
   */
  const { filterModel, onFilterModelChange, serverSearchTerm } =
    useServerBackedQuickFilter();

  /**
   * A change to any of these re-scopes the whole result set, so the page index
   * in hand means nothing against it — page four of the full book is usually
   * past the end of a search.
   *
   * Done during render rather than in an effect so the query below already sees
   * page one on the render that carries the change. From an effect the reset
   * lands a render too late, and a request goes out for the stale page against
   * the new scope before being immediately superseded.
   */
  const datasetKey = [
    accountId,
    endDate,
    excludedAccounts?.join(','),
    excludedAccountTypes?.join(','),
    excludedCategories?.join(','),
    groupBy,
    investorId,
    onlyPrimaryInvestors,
    serverSearchTerm,
  ].join('|');
  const previousDatasetKey = useRef(datasetKey);
  if (previousDatasetKey.current !== datasetKey) {
    previousDatasetKey.current = datasetKey;
    if (paginationModel.page !== 0) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
    }
  }

  const params = useMemo(() => {
    const grouped = groupBy !== 'none';
    const p: HoldingsQueryParams = {
      searchTerm: serverSearchTerm,
      asOfDate: endDate,
    };
    // Backend paging conventions are inconsistent across WA endpoints.
    // The holdings list endpoint expects `Page`/`Limit` (1-based) rather than `page`/`pageSize`.
    // When grouped, request the full set so client-side group rollups aggregate
    // across every row instead of just the current page.
    // HoldingsQueryParams carries an index signature for these bracketed/extra
    // keys, so they can be assigned directly without an `any` cast.
    p.Page = grouped ? 1 : paginationModel.page + 1;
    p.Limit = grouped ? ALL_HOLDINGS_LIMIT : paginationModel.pageSize;
    p['sort[0].Property'] = 'Balance';
    p['sort[0].Direction'] = 'desc';
    p['sort[0].Order'] = 0;
    if (accountId) {
      p.accountId = accountId;
    }
    if (typeof investorId === 'number') {
      p.InvestorId = investorId;
    }
    if (typeof onlyPrimaryInvestors !== 'undefined') {
      p.OnlyPrimaryInvestors = onlyPrimaryInvestors;
    }
    if (excludedAccounts?.length) {
      p.ExcludedAccounts = excludedAccounts.join(',');
    }
    if (excludedAccountTypes?.length) {
      p.ExcludedAccountTypes = excludedAccountTypes.join(',');
    }
    if (excludedCategories?.length) {
      p.ExcludedCategories = excludedCategories.join(',');
    }
    return p;
  }, [
    endDate,
    accountId,
    excludedAccounts,
    excludedAccountTypes,
    excludedCategories,
    investorId,
    onlyPrimaryInvestors,
    groupBy,
    paginationModel.page,
    paginationModel.pageSize,
    serverSearchTerm,
  ]);

  // The shared RTK query hooks surface `data` loosely, so assert each response to
  // its concrete shape here. The rows/total/tag logic below then stays type-safe
  // without any `any` leaking through.
  const {
    data: holdingsResponse,
    isLoading: holdingsLoading,
    isFetching: holdingsFetching,
    isError: holdingsError,
  } = useGetHoldingsQuery(authLoaded ? params : skipToken);
  const holdingsData = holdingsResponse as
    | RecordResponse<HoldingViewModel>
    | undefined;
  const isLoading = Boolean(holdingsLoading);
  const isFetching = Boolean(holdingsFetching);
  const isError = Boolean(holdingsError);

  const { data: accountsResponse } = useGetAccountsQuery(
    authLoaded
      ? {
          showClosed: false,
          asOfDate: endDate,
          ...(typeof investorId === 'number' ? { InvestorId: investorId } : {}),
        }
      : skipToken,
  );
  const accountsData = accountsResponse as
    | RecordResponse<{ AccountId: number; Tags?: Record<string, string> }>
    | undefined;

  const accountTagMap = useMemo(() => {
    // Value is optional: a holding's account may not be present in the map.
    const map: Record<number, Record<string, string> | undefined> = {};
    accountsData?.Records.forEach(
      (account: { AccountId: number; Tags?: Record<string, string> }) => {
        map[account.AccountId] = account.Tags || {};
      },
    );
    return map;
  }, [accountsData]);

  useEffect(() => {
    if (holdingsData?.Records) {
      onDataLoaded?.();
    }
  }, [holdingsData, onDataLoaded]);

  const { tickerUrlFormatter } = useTickerUrlFormatter();

  const rows: HoldingRow[] = useMemo(() => {
    if (!holdingsData?.Records) return [];

    return holdingsData.Records.map((item: HoldingViewModel) => ({
      id: item.HoldingId,
      ...item,
      UnrealizedGains: getUnrealizedGains(item),
      CustomGroup:
        groupBy !== 'none' && groupBy !== 'account'
          ? accountTagMap[item.AccountId]?.[groupBy] || t('notSet')
          : undefined,
    }));
  }, [holdingsData, groupBy, accountTagMap, t]);

  const total = useMemo(() => {
    // Prefer backend-provided overall total when available; otherwise fall back to current page sum.
    const summed = holdingsData?.SummedValue;
    if (typeof summed === 'number' && Number.isFinite(summed)) {
      return summed;
    }
    if (!holdingsData?.Records) return 0;
    return holdingsData.Records.reduce(
      (acc: number, item: HoldingViewModel) => acc + item.Balance,
      0,
    );
  }, [holdingsData]);

  const handleHoldingSelect = useCallback((item: HoldingViewModel) => {
    // EUD-894: group rows hand the grid an empty `{}` row model; never open the
    // flyout for a row that doesn't identify a real holding.
    if (!Number.isFinite(item.HoldingId) || item.HoldingId <= 0) return;
    setSelectedItem(item);
    setIsFlyoutOpen(true);
  }, []);

  const handleFlyoutClose = useCallback(() => {
    setIsFlyoutOpen(false);
    setSelectedItem(null);
  }, []);

  const advisorContext = useMemo(
    () =>
      buildHoldingAdvisorContext({
        clientName: advisorClientName,
        selectedHolding: selectedItem,
        getAccountById,
        onClientNameClick: handleFlyoutClose,
      }),
    [advisorClientName, selectedItem, getAccountById, handleFlyoutClose],
  );

  const groupingModel = useMemo(() => {
    if (groupBy === 'account') return ['AccountName'];
    if (groupBy !== 'none') return ['CustomGroup'];
    return [];
  }, [groupBy]);

  // When grouped we load the full dataset (see ALL_HOLDINGS_LIMIT) and let the grid
  // paginate client-side, so aggregation spans every row. When ungrouped we keep
  // server-side pagination.
  const isGrouped = groupBy !== 'none';

  const [aggregationModel, setAggregationModel] =
    useState<GridAggregationModel>(() =>
      groupBy === 'none' ? {} : HOLDINGS_ROLLUP_AGGREGATION_MODEL,
    );

  // Update aggregation model when grouping changes
  useEffect(() => {
    if (groupBy === 'none') {
      setAggregationModel({});
    } else {
      setAggregationModel(HOLDINGS_ROLLUP_AGGREGATION_MODEL);
    }
  }, [groupBy]);

  const initialColumnVisibilityModel: GridColumnVisibilityModel = {
    EstimatedIncome: false,
    MaturityDate: !isMobile,
    InterestRate: !isMobile,
    AnnualInterestAmount: !isMobile,
    CostBasis: !isMobile,
    UnrealizedGains: !isMobile,
    UnrealizedGainsPercent: !isMobile,
    Classifications: !isMobile,
    YieldToMarket: !isMobile,
    CurrentYield: !isMobile,
    YieldToMaturity: !isMobile,
  };

  const baseInitialState = {
    columns: {
      columnVisibilityModel: initialColumnVisibilityModel,
    },
  };

  const initialState = useKeepGroupedColumnsHidden({
    apiRef,
    rowGroupingModel: groupingModel,
    initialState: {
      rowGrouping: { model: groupingModel },
      sorting: {
        sortModel: BALANCE_DESC_GRID_SORT_MODEL,
      },
      ...baseInitialState,
    },
  });

  const HoldingListViewCell = (params: GridRenderCellParams<HoldingRow>) => {
    const { row, rowNode, api } = params;

    // Group header row when grouping is enabled (matches Accounts list-view behavior)
    if (rowNode.type === 'group') {
      // The grid tree node carries grouping metadata the base GridTreeNode type
      // doesn't surface; read it through a narrow, concrete shape.
      const groupNode = rowNode as {
        groupingKey?: string | number | null;
        descendantCount?: number;
        children?: GridRowId[];
        childrenIds?: GridRowId[];
        childrenExpanded?: boolean;
        aggregation?: {
          Balance?: { sum?: number };
          Price?: { sum?: number };
        };
      };

      const groupKey = String(
        groupNode.groupingKey ?? t('grid.notGrouped', 'Not Grouped'),
      );

      const descendantCountFromRowNode = groupNode.descendantCount ?? 0;

      const childrenCountFromRowNode = Array.isArray(groupNode.children)
        ? groupNode.children.length
        : Array.isArray(groupNode.childrenIds)
          ? groupNode.childrenIds.length
          : 0;

      const node = gridRowNodeSelector(apiRef, params.id) as {
        descendantCount?: number;
      } | null;
      const descendantCountFromApi = node?.descendantCount ?? 0;

      const groupCount =
        descendantCountFromRowNode ||
        childrenCountFromRowNode ||
        descendantCountFromApi ||
        0;

      const isExpanded = !!groupNode.childrenExpanded;

      const handleToggleExpand = (event: React.MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        apiRef.current?.setRowChildrenExpansion(params.id, !isExpanded);
      };

      const groupAggregation = groupNode.aggregation;

      const sumBalance = groupAggregation?.Balance?.sum;
      const sumPrice = groupAggregation?.Price?.sum;

      return (
        <Box
          sx={{
            width: '100%',
            p: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              minWidth: 0,
            }}
          >
            <Box
              onClick={handleToggleExpand}
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                p: 1,
                borderRadius: 1,
                '&:hover': {
                  backgroundColor: (theme) => theme.palette.action.hover,
                },
              }}
              aria-label={
                isExpanded
                  ? t('grid.collapseGroup', 'Collapse group')
                  : t('grid.expandGroup', 'Expand group')
              }
            >
              {isExpanded ? (
                <ExpandMoreIcon fontSize='small' />
              ) : (
                <ChevronRightIcon fontSize='small' />
              )}
            </Box>

            <Typography
              variant='body1'
              sx={{
                fontWeight: '600 !important',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {rowNode.groupingField === 'AccountName' &&
              canOpenAccountByName(groupKey) ? (
                <Box
                  component='span'
                  onClick={(event) => {
                    event.stopPropagation();
                    openAccountByName(groupKey);
                  }}
                  sx={{ cursor: 'pointer', textDecoration: 'underline' }}
                >
                  {groupKey}
                </Box>
              ) : (
                groupKey
              )}
              {groupCount > 0 ? ` (${String(groupCount)})` : ''}
            </Typography>
          </Box>

          {sumBalance != null ? (
            <Typography
              variant='body1'
              sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              {formatNumberToCurrency(sumBalance)}
            </Typography>
          ) : sumPrice != null ? (
            <Typography
              variant='body1'
              sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}
            >
              {formatNumberToCurrency(sumPrice)}
            </Typography>
          ) : null}
        </Box>
      );
    }

    // Read field values through the null-honest view (see NullableHoldingRow):
    // the API returns null for fields the model types as non-null, so the guards
    // and fallbacks below are real defenses, not redundant checks.
    const h: NullableHoldingRow = row;

    const visibilityModel = api.state.columns.columnVisibilityModel;
    const visibleColumns = api
      .getAllColumns()
      .filter(
        (column) =>
          column.field !== 'listColumn' &&
          // Columns absent from the model default to visible; only an explicit
          // `false` hides them. Read as boolean|undefined so the check stays
          // meaningful (the model's index type omits the undefined case).
          (visibilityModel[column.field] as boolean | undefined) !== false,
      )
      .map((column) => column.field);

    const lastUpdatedValue = formatAppDate(row.UpdatedDate, {
      format: 'M/D/YY',
      fallback: '',
    });

    const sharesAtPriceValue =
      h.Quantity != null && h.Price != null
        ? t('grid.sharesAtPrice', {
            quantity: h.Quantity.toLocaleString('en-US', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }),
            price: formatNumberToCurrency(h.Price),
          })
        : '';

    const getTrimmedValue = (value: string) => {
      const v = value.trim();
      return v ? v : null;
    };

    const accountNameValue = getTrimmedValue(h.AccountName ?? '');
    const sharesAtPriceLineValue = getTrimmedValue(sharesAtPriceValue);
    const lastUpdatedLineValue = getTrimmedValue(lastUpdatedValue);
    const unrealizedGainsPercentValue = hasUsableCostBasis(h)
      ? `${(((h.Balance - h.CostBasis) / h.CostBasis) * 100).toFixed(2)}%`
      : 'N/A';
    const optionalListViewLines = [
      {
        field: 'CostBasis',
        label: t('grid.costBasis'),
        value:
          h.CostBasis == null ? 'N/A' : formatNumberToCurrency(h.CostBasis),
      },
      {
        field: 'UnrealizedGains',
        label: t('grid.unrealizedGainLoss'),
        value:
          h.UnrealizedGains == null
            ? 'N/A'
            : formatNumberToCurrency(h.UnrealizedGains),
      },
      {
        field: 'UnrealizedGainsPercent',
        label: t('grid.gainLossPercent'),
        value: unrealizedGainsPercentValue,
      },
      {
        field: 'Classifications',
        label: t('grid.classifications'),
        value: (h.Classifications ?? '').trim(),
      },
      {
        field: 'YieldToMarket',
        label: t('grid.yieldToMarket'),
        value:
          h.YieldToMarket == null || h.YieldToMarket === 0
            ? ''
            : `${h.YieldToMarket.toFixed(2)}%`,
      },
      {
        field: 'CurrentYield',
        label: t('grid.currentYield'),
        value:
          h.CurrentYield == null || h.CurrentYield === 0
            ? ''
            : `${h.CurrentYield.toFixed(2)}%`,
      },
      {
        field: 'YieldToMaturity',
        label: t('grid.yieldToMaturity'),
        value:
          h.YieldToMaturity == null || h.YieldToMaturity === 0
            ? ''
            : `${h.YieldToMaturity.toFixed(2)}%`,
      },
      {
        field: 'EstimatedIncome',
        label: t('grid.estimatedIncome'),
        value:
          h.EstimatedIncome == null
            ? '—'
            : formatNumberToCurrency(h.EstimatedIncome),
      },
      {
        field: 'MaturityDate',
        label: t('grid.maturityDate'),
        value: formatAppDate(row.MaturityDate, { format: 'M/D/YY' }),
      },
      {
        field: 'InterestRate',
        label: t('grid.interestRate'),
        value: h.InterestRate == null ? '—' : `${h.InterestRate.toFixed(2)}%`,
      },
      {
        field: 'AnnualInterestAmount',
        label: t('grid.annualInterestAmount'),
        value:
          h.AnnualInterestAmount == null
            ? '—'
            : formatNumberToCurrency(h.AnnualInterestAmount),
      },
      {
        field: 'Symbol',
        label: t('grid.symbol', 'Symbol'),
        value: (h.Symbol ?? '').trim(),
      },
    ];

    return (
      <Box
        sx={{
          width: '100%',
          p: 2,
          px: 0,
          // Right padding so the amount clears the vertical scrollbar in the
          // narrow/half-size widget list view (EUD-753).
          pr: 2,
          cursor: 'pointer',
        }}
        onClick={() => {
          handleHoldingSelect(row);
        }}
      >
        {/* Line 1: Title (left) + Balance (right) */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'left',
            gap: 2,
            mb: 0.75,
            width: '100%',
          }}
        >
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant='body1'
              sx={{
                fontWeight: 600,
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                lineHeight: 1.2,
              }}
            >
              <b>{h.Name ?? ''}</b>
            </Typography>
          </Box>

          <Typography
            variant='body1'
            sx={{
              fontWeight: '700 !important',
              whiteSpace: 'nowrap',
            }}
          >
            {formatNumberToCurrency(h.Balance ?? 0)}
          </Typography>
        </Box>

        {/* Lines 2+: Title (left) + Value (right) formatting */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 0.5,
          }}
        >
          {/*
            ListViewLine: label left, value right.
            - label uses flex: 1 so the value can right-align and stay on one line
            - value truncates with ellipsis when needed
          */}
          {(() => {
            return (
              <>
                {visibleColumns.includes('AccountName') && accountNameValue ? (
                  <Typography variant='body2'>{accountNameValue}</Typography>
                ) : null}

                {(visibleColumns.includes('Quantity') ||
                  visibleColumns.includes('Price')) &&
                sharesAtPriceLineValue ? (
                  <Typography
                    variant='body2'
                    sx={{
                      minWidth: 0,
                      color: 'text.secondary',
                      textAlign: 'left',
                    }}
                  >
                    {sharesAtPriceLineValue}
                  </Typography>
                ) : null}

                {visibleColumns.includes('UpdatedDate') &&
                lastUpdatedLineValue ? (
                  <Typography
                    variant='body2'
                    sx={{
                      minWidth: 0,
                      color: 'text.secondary',
                      textAlign: 'left',
                    }}
                  >
                    {t('grid.lastUpdated', 'Last Updated')}{' '}
                    {lastUpdatedLineValue}
                  </Typography>
                ) : null}

                {optionalListViewLines.map((line) =>
                  visibleColumns.includes(line.field) && line.value ? (
                    <Typography
                      key={line.field}
                      variant='body2'
                      sx={{
                        minWidth: 0,
                        color: 'text.secondary',
                        textAlign: 'left',
                      }}
                    >
                      {line.label} {line.value}
                    </Typography>
                  ) : null,
                )}
              </>
            );
          })()}
        </Box>
      </Box>
    );
  };

  const listViewColDef: GridListViewColDef<HoldingRow> = {
    field: 'listColumn',
    renderCell: HoldingListViewCell,
  };

  const getAdaptiveRowHeight = useCallback(() => 'auto' as const, []);
  const getEstimatedAdaptiveRowHeight = useCallback(
    () => (isXs ? 148 : 84),
    [isXs],
  );
  const renderCurrencyCell = useCallback(
    (
      params: GridRenderCellParams<HoldingRow, number>,
      nullPlaceholder: CurrencyCellPlaceholder,
    ) => {
      if (params.value == null) return nullPlaceholder;
      return formatNumberToCurrency(params.value);
    },
    [],
  );

  const columns: GridColDef<HoldingRow>[] = useMemo(
    () => [
      {
        field: 'CustomGroup',
        headerName: t('grid.group'),
        flex: 1,
        hideable: false,
        groupable: true,
      },
      {
        field: 'Name',
        headerName: t('name'),
        flex: 1,
        minWidth: GRID_COLUMN_WIDTH_NARROW,
        maxWidth: 250,
        cellClassName: 'holdings-name-cell',
        renderCell: (params: GridRenderCellParams<HoldingRow>) => {
          // EUD-894: when grouped, the parent (group) row renders this cell too,
          // with an empty row model. The group label lives in the grouping column,
          // so render nothing here rather than a clickable cell that would open an
          // empty holding flyout.
          if (params.rowNode.type === 'group') {
            return null;
          }
          const row: NullableHoldingRow = params.row;
          const accountName = row.AccountName ?? '';
          return (
            <Box
              sx={{
                whiteSpace: 'normal',
                wordBreak: 'break-word',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                width: '100%',
                lineHeight: 1.2,
                cursor: 'pointer',
                py: 0,
              }}
              onClick={() => {
                handleHoldingSelect(params.row);
              }}
            >
              <Typography
                variant='body2'
                color='text.primary'
                sx={{
                  fontWeight: 400,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                {params.value}
              </Typography>
              <Typography
                variant='caption'
                color='text.secondary'
                sx={{
                  fontWeight: 400,
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                }}
              >
                {row.InstitutionName}: {accountName}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'InstitutionName',
        headerName: t('grid.institutionName'),
        flex: 1,
        minWidth: GRID_COLUMN_WIDTH_NARROW,
        maxWidth: 250,
      },
      {
        field: 'AccountName',
        headerName: t('grid.accountName'),
        flex: 1,
        minWidth: GRID_COLUMN_WIDTH_NARROW,
        maxWidth: 250,
        // EUD-440: clicking the account name opens the Account Details flyout.
        renderCell: (params: GridRenderCellParams<HoldingRow>) => {
          // When grouping by account, the grid delegates the group-header value to
          // this renderCell; group nodes have no AccountName field, so render the
          // grouping key (the account name) plainly instead of blanking the header.
          if (params.rowNode.type === 'group') {
            // This is the AccountName column; it only has a value for account
            // groups. For any other grouping field the group row's cell here is
            // empty so it doesn't echo the other group's key (EUD-440).
            if (params.rowNode.groupingField !== 'AccountName') {
              return null;
            }
            const key = params.rowNode.groupingKey;
            if (key == null) return null;
            const label = String(key);
            if (!canOpenAccountByName(label)) {
              return <Typography variant='body2'>{label}</Typography>;
            }
            return (
              <Typography
                variant='body2'
                onClick={(event) => {
                  event.stopPropagation();
                  openAccountByName(label);
                }}
                sx={{ cursor: 'pointer', textDecoration: 'underline' }}
              >
                {label}
              </Typography>
            );
          }
          const r: NullableHoldingRow = params.row;
          const name = r.AccountName ?? '';
          if (!name) return null;
          if (!canOpenAccount(r.AccountId)) {
            return <Typography variant='body2'>{name}</Typography>;
          }
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                height: '100%',
                cursor: 'pointer',
              }}
              onClick={() => {
                openAccountById(r.AccountId);
              }}
            >
              <Typography
                variant='body2'
                sx={{ '&:hover': { textDecoration: 'underline' } }}
              >
                {name}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'Quantity',
        headerName: t('grid.quantity'),
        type: 'number',
        renderCell: (params) => {
          if (params.value == null) return '';
          return Number(params.value).toLocaleString('en-US', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          });
        },
        width: isMobile
          ? HOLDINGS_COL_WIDTH_COMPACT_MOBILE
          : HOLDINGS_COL_WIDTH_COMPACT_DESKTOP,
      },
      {
        field: 'Price',
        headerName: t('grid.price'),
        type: 'number',
        valueFormatter: (value: number | null) => {
          if (value == null) {
            return '';
          }
          return formatNumberToCurrency(value);
        },
        aggregable: true,
        renderCell: (params: GridRenderCellParams<HoldingRow, number>) => {
          // Handle aggregation values
          if (params.rowNode.type === 'group') {
            const aggregatedValue = params.value;
            if (aggregatedValue != null) {
              return (
                <Box
                  sx={{
                    fontWeight: 600,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    height: '100%',
                    width: '100%',
                    textAlign: 'right',
                  }}
                >
                  {formatNumberToCurrency(aggregatedValue)}
                </Box>
              );
            }
          }
          // Regular cell rendering
          if (params.value == null) return '';
          return formatNumberToCurrency(params.value);
        },
        align: 'right',
        headerAlign: 'right',
        width: isMobile
          ? HOLDINGS_COL_WIDTH_COMPACT_MOBILE
          : HOLDINGS_COL_WIDTH_COMPACT_DESKTOP,
      },
      {
        field: 'Balance',
        headerName: t('grid.balance'),
        type: 'number',
        aggregable: true,
        renderCell: (params: GridRenderCellParams<HoldingRow, number>) =>
          renderCurrencyCell(params, ''),
        width: isMobile
          ? HOLDINGS_COL_WIDTH_CURRENCY_MOBILE
          : GRID_COLUMN_WIDTH_NARROW,
      },
      {
        field: 'CostBasis',
        headerName: t('grid.costBasis'),
        type: 'number',
        aggregable: true,
        renderCell: (params: GridRenderCellParams<HoldingRow, number>) =>
          renderCurrencyCell(params, 'N/A'),
        width: isMobile
          ? HOLDINGS_COL_WIDTH_CURRENCY_NARROW_MOBILE
          : GRID_COLUMN_WIDTH_NARROW,
      },
      {
        field: 'UnrealizedGains',
        headerName: t('grid.unrealizedGainLoss'),
        type: 'number',
        aggregable: true,
        valueFormatter: formatUnrealizedGainsForExport,
        getSortComparator: getNullsLastSortComparator,
        renderCell: (params: GridRenderCellParams<HoldingRow, number>) =>
          renderCurrencyCell(params, 'N/A'),
        width: isMobile
          ? HOLDINGS_COL_WIDTH_CURRENCY_MOBILE
          : HOLDINGS_COL_WIDTH_GAIN_LOSS_DESKTOP,
      },
      {
        field: 'UnrealizedGainsPercent',
        headerName: t('grid.gainLossPercent'),
        type: 'number',
        aggregable: true,
        renderCell: (params: GridRenderCellParams<HoldingRow, number>) => {
          if (params.rowNode.type === 'group') {
            if (params.value == null) return 'N/A';
            return `${params.value.toFixed(2)}%`;
          }
          const r: NullableHoldingRow = params.row;
          if (!hasUsableCostBasis(r)) return 'N/A';
          const percentage = ((r.Balance - r.CostBasis) / r.CostBasis) * 100;
          return `${percentage.toFixed(2)}%`;
        },
        width: isMobile
          ? HOLDINGS_COL_WIDTH_PERCENT_MOBILE
          : HOLDINGS_COL_WIDTH_COMPACT_DESKTOP,
      },
      {
        field: 'Classifications',
        headerName: t('grid.classifications'),
        renderCell: (params: GridRenderCellParams<HoldingRow, string>) => {
          if (params.value == null) return '';
          return params.value;
        },
        width: isMobile
          ? GRID_COLUMN_WIDTH_NARROW
          : HOLDINGS_COL_WIDTH_CLASSIFICATIONS_DESKTOP,
      },
      {
        field: 'YieldToMarket',
        headerName: t('grid.yieldToMarket'),
        width: HOLDINGS_COL_WIDTH_YIELD,
        sortable: true,
        renderCell: (params: GridRenderCellParams<HoldingRow>) => {
          if (params.rowNode.type === 'group') {
            return '';
          }
          const v = params.row.YieldToMarket;
          if (v == null || v === 0) {
            return '';
          }
          return `${v.toFixed(2)}%`;
        },
      },
      {
        field: 'CurrentYield',
        headerName: t('grid.currentYield'),
        width: HOLDINGS_COL_WIDTH_YIELD,
        sortable: true,
        renderCell: (params: GridRenderCellParams<HoldingRow>) => {
          if (params.rowNode.type === 'group') {
            return '';
          }
          const v = params.row.CurrentYield;
          if (v == null || v === 0) {
            return '';
          }
          return `${v.toFixed(2)}%`;
        },
      },
      {
        field: 'YieldToMaturity',
        headerName: t('grid.yieldToMaturity'),
        width: HOLDINGS_COL_WIDTH_YIELD,
        sortable: true,
        renderCell: (params: GridRenderCellParams<HoldingRow>) => {
          if (params.rowNode.type === 'group') {
            return '';
          }
          const v = params.row.YieldToMaturity;
          if (v == null || v === 0) {
            return '';
          }
          return `${v.toFixed(2)}%`;
        },
      },
      {
        field: 'EstimatedIncome',
        headerName: t('grid.estimatedIncome'),
        type: 'number',
        aggregable: true,
        renderCell: (params: GridRenderCellParams<HoldingRow, number>) =>
          renderCurrencyCell(params, '—'),
        width: GRID_COLUMN_WIDTH_NARROW,
      },
      {
        field: 'MaturityDate',
        headerName: t('grid.maturityDate'),
        type: 'date',
        valueGetter: (value: string) => parseOptionalDate(value),
        renderCell: (params: GridRenderCellParams<HoldingRow, Date>) => {
          return formatAppDate(params.value, { format: 'M/D/YY' });
        },
        width: GRID_COLUMN_WIDTH_NARROW,
      },
      {
        field: 'InterestRate',
        headerName: t('grid.interestRate'),
        type: 'number',
        renderCell: (params) => {
          if (params.value == null) return '—';
          return `${Number(params.value).toFixed(2)}%`;
        },
        width: HOLDINGS_COL_WIDTH_COMPACT_DESKTOP,
      },
      {
        field: 'AnnualInterestAmount',
        headerName: t('grid.annualInterestAmount'),
        type: 'number',
        renderCell: (params) => {
          if (params.value == null) return '—';
          return formatNumberToCurrency(Number(params.value));
        },
        width: GRID_COLUMN_WIDTH_NARROW,
      },
      {
        field: 'Symbol',
        headerName: t('grid.symbol', 'Symbol'),
        width: HOLDINGS_COL_WIDTH_SYMBOL,
        renderCell: (params: GridRenderCellParams<HoldingRow>) => {
          return tickerUrlFormatter(params.value);
        },
      },
      {
        field: 'UpdatedDate',
        type: 'date',
        valueGetter: (_value: string, row: HoldingRow) => {
          return parseOptionalDate(row.UpdatedDate);
        },
        renderCell: (params: GridRenderCellParams<HoldingRow, Date>) => {
          return formatAppDate(params.value, {
            format: 'M/D/YY',
            fallback: '',
          });
        },
        headerName: t('grid.lastUpdated'),
        width: isMobile
          ? HOLDINGS_COL_WIDTH_LAST_UPDATED_MOBILE
          : HOLDINGS_COL_WIDTH_LAST_UPDATED_DESKTOP,
      },
    ],
    [
      t,
      isMobile,
      tickerUrlFormatter,
      handleHoldingSelect,
      renderCurrencyCell,
      canOpenAccount,
      openAccountById,
      canOpenAccountByName,
      openAccountByName,
    ],
  );

  // Merge API-provided columns that are not present in the hard-coded list
  const mergedColumns: GridColDef<HoldingRow>[] = useMemo(() => {
    const records = gridColumnsData?.Records ?? [];
    if (!records.length) return columns;

    const staticFieldsNorm = new Set(
      columns.map((c) => normalizeFieldKey(c.field)),
    );
    const staticHeadersNorm = new Set(
      columns.map((c) => normalizeFieldKey(c.headerName ?? c.field)),
    );

    const additionalColumns = records
      .filter((r) => {
        const fieldKey = normalizeFieldKey(r.GridColumnDataField);
        const labelKey = normalizeFieldKey(r.Label || r.GridColumnDataField);
        return (
          fieldKey !== 'lastupdated' &&
          !staticFieldsNorm.has(fieldKey) &&
          !staticHeadersNorm.has(labelKey)
        );
      })
      .sort((a, b) => a.Order - b.Order)
      .map((r) => {
        return {
          field: (r.GridColumnDataField || '').trim(),
          headerName: r.Label || r.GridColumnDataField,
          width: GRID_COLUMN_WIDTH_NARROW,
        } as GridColDef<HoldingRow>;
      });

    // Insert dynamic additional columns immediately after the static "AnnualInterestAmount" column
    // so that the static "UpdatedDate" column remains last in this table
    const insertAfterField = 'AnnualInterestAmount';
    const insertIndex = columns.findIndex((c) => c.field === insertAfterField);
    if (insertIndex >= 0) {
      const before = columns.slice(0, insertIndex + 1);
      const after = columns.slice(insertIndex + 1);
      return [...before, ...additionalColumns, ...after];
    }
    return [...columns, ...additionalColumns];
  }, [columns, gridColumnsData]);

  const baseColumns = useMemo(() => {
    let cols = isMobile
      ? mergedColumns
      : mergedColumns.filter((c) => c.field !== 'InstitutionName');

    if (!isMobile && groupBy !== 'account') {
      cols = cols.filter((c) => c.field !== 'AccountName');
    }

    if (groupBy === 'none') {
      cols = cols.filter((c) => c.field !== 'CustomGroup');
    }

    return cols;
  }, [isMobile, mergedColumns, groupBy]);

  const finalColumns = useMemo(
    () => applyColumnVisibility(baseColumns),
    [baseColumns, applyColumnVisibility],
  );

  // Auto-resize first column when grouping changes and columns are loaded
  useAutoResizeGroupingColumn({
    apiRef,
    columnsForDisplay: columns,
    rowGroupingModel: isMobile ? [] : groupingModel,
  });

  /**
   * The error message replaces the grid, and with it the toolbar the search box
   * lives in. A request that failed while a term was active would otherwise
   * leave nothing able to change that term, so the term is offered up for
   * clearing alongside the message.
   */
  if (isError) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Typography color='error'>
          {t('errorLoadingHoldingsData', 'Error loading holdings data.')}
        </Typography>
        {serverSearchTerm ? (
          <Button
            variant='outlined'
            size='small'
            sx={{ mt: 2 }}
            onClick={() => {
              onFilterModelChange({ items: [], quickFilterValues: [] });
            }}
          >
            {t('clearSearch', 'Clear search')}
          </Button>
        ) : null}
      </Box>
    );
  }

  /**
   * An empty book replaces the grid with a message. A search that matched
   * nothing must not, because the search box lives in the grid's toolbar and
   * taking it away leaves no way to clear the term. The grid's own empty
   * overlay covers that case instead.
   */
  if (
    (!holdingsData?.Records || holdingsData.Records.length === 0) &&
    !serverSearchTerm &&
    !isLoading &&
    !isFetching &&
    authLoaded
  ) {
    return (
      <Box sx={{ width: '100%', p: 2 }}>
        <Typography>
          {t('noHoldingsDataAvailable', 'No holdings data available.')}
        </Typography>
      </Box>
    );
  }

  return (
    <Box ref={widgetWidthRef}>
      <GridContainer
        isWidget={_isWidget}
        size={size}
      >
        <DataGridPremium
          key={
            shouldUseListView ? `holdings-grid-xs-${groupBy}` : 'holdings-grid'
          }
          rows={rows}
          columns={finalColumns}
          apiRef={apiRef}
          disableRowSelectionOnClick
          showToolbar
          disablePivoting
          pagination
          paginationMode={isGrouped ? 'client' : 'server'}
          filterModel={filterModel}
          onFilterModelChange={onFilterModelChange}
          filterMode='server'
          disableColumnFilter
          rowCount={isGrouped ? undefined : (holdingsData?.TotalLength ?? 0)}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pageSizeOptions={[...PAGE_SIZE_OPTIONS.STANDARD]}
          getRowHeight={getAdaptiveRowHeight}
          getEstimatedRowHeight={getEstimatedAdaptiveRowHeight}
          listView={shouldUseListView}
          {...(shouldUseListView ? { listViewColumn: listViewColDef } : {})}
          initialState={{
            ...initialState,
            pagination: {
              paginationModel: { pageSize: PAGE_SIZES.DEFAULT, page: 0 },
            },
          }}
          rowGroupingModel={groupingModel}
          onRowGroupingModelChange={(newModel) => {
            // Update aggregation when grouping changes
            if (newModel.length === 0) {
              setAggregationModel({});
            } else {
              setAggregationModel(HOLDINGS_ROLLUP_AGGREGATION_MODEL);
            }
          }}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={handleColumnVisibilityChange}
          groupingColDef={{
            hideDescendantCount: false,
            width: undefined, // Let it auto-size
            flex: 1,
            minWidth: HOLDINGS_COL_WIDTH_CLASSIFICATIONS_DESKTOP,
          }}
          aggregationFunctions={{
            ...GRID_AGGREGATION_FUNCTIONS,
            holdingsGainLossPercent:
              createHoldingsGainLossPercentAggregation(t),
            holdingsUnrealizedGains: unrealizedGainsAggregation,
          }}
          aggregationModel={aggregationModel}
          onAggregationModelChange={setAggregationModel}
          // MUI also calls this for the root group (the grid-level aggregate,
          // passed as null here) in addition to each row group. Return null for
          // the root so a grouped view doesn't add an all-rows aggregate on top
          // of the per-group sums (EUD-440 review).
          getAggregationPosition={(groupNode: GridGroupNode | null) =>
            groupNode == null ? null : 'inline'
          }
          // Default 'filtered' scope so group rollups reflect only the rows that
          // pass the active filter/quick-filter, matching the displayed children.
          aggregationRowsScope='filtered'
          slotProps={{
            loadingOverlay: {
              variant: 'linear-progress',
              noRowsVariant: 'skeleton',
            },
            toolbar: {
              ...defaultToolbarExportSlotProps,
              showQuickFilter: true,
              quickFilterProps: { debounceMs: DEBOUNCE_MS.NONE },
              showFilters: false,
            },
            columnsManagement: { defaultColumnVisibilityModel },
          }}
          slots={{
            columnsManagement: ManagedColumnsManagement,
            noRowsOverlay: NoHoldingsRowsOverlay,
            noResultsOverlay: NoHoldingsRowsOverlay,
            ...(isNative
              ? {
                  toolbar: (
                    props: React.ComponentProps<typeof CustomToolbarForNative>,
                  ) => <CustomToolbarForNative {...props} />,
                }
              : shouldUseListView
                ? { toolbar: XsPreferencesDrawerToolbar }
                : {}),
          }}
          loading={isLoading || isFetching || !authLoaded || gridColumnsLoading}
          sx={{
            ...(_isWidget
              ? { height: '100%' }
              : {
                  minHeight: typeof gridHeight === 'number' ? gridHeight : 400,
                }),
            ...(shouldUseListView && {
              // Fill the widget / narrow container; list view must not shrink to max-content.
              width: '100%',
              maxWidth: '100%',
              minWidth: 0,
              alignSelf: 'stretch',
              // When column autosizing runs, the grid toggles `MuiDataGrid-autosizing` and can apply
              // competing width rules (e.g. max-content) that prevent full-width list cells.
              '&.MuiDataGrid-autosizing .MuiDataGrid-cell': {
                width: '100% !important',
                minWidth: '100% !important',
                maxWidth: '100% !important',
              },
              '&.MuiDataGrid-autosizing .MuiDataGrid-row': {
                width: '100% !important',
                minWidth: '100% !important',
                maxWidth: '100% !important',
              },
              // In list view, force the single list column to actually fill the grid width.
              // This prevents "phantom second columns" caused by empty filler cells / stale width calc
              // when the grid is inside constrained widget layouts.
              '& .MuiDataGrid-row:not(.MuiDataGrid-row--group) .MuiDataGrid-cellEmpty':
                {
                  display: 'none !important',
                },
              // Hide the generated grouping column on XS list view. Group headers are rendered
              // inside `HoldingListViewCell` and include their own expand/collapse toggle.
              "& .MuiDataGrid-columnHeader[data-field='__row_group_by_columns_group__']":
                {
                  display: 'none !important',
                },
              "& .MuiDataGrid-cell[data-field='__row_group_by_columns_group__']":
                {
                  display: 'none !important',
                },
              '& .MuiDataGrid-main': { width: '100%' },
              '& .MuiDataGrid-virtualScroller': { width: '100%' },
              '& .MuiDataGrid-virtualScrollerRenderZone': {
                width: '100% !important',
                minWidth: '100% !important',
                maxWidth: '100% !important',
              },
              '& .MuiDataGrid-virtualScrollerContent': {
                width: '100% !important',
              },
              '& .MuiDataGrid-virtualScrollerContent > div': {
                width: '100% !important',
                maxWidth: '100% !important',
              },
              '& .MuiDataGrid-row': {
                display: 'flex',
                flexWrap: 'nowrap',
                width: '100% !important',
                minWidth: '100% !important',
                maxWidth: '100% !important',
              },
              '& .MuiDataGrid-cell': {
                display: 'flex',
                flexWrap: 'nowrap',
                width: '100% !important',
                minWidth: '100% !important',
                maxWidth: '100% !important',
                flex: '1 1 0',
              },
              '& .MuiDataGrid-cellContent': {
                width: '100%',
                flex: 1,
                minWidth: 0,
              },
              [theme.breakpoints.down(800)]: {
                '& .MuiDataGrid-cell': {
                  width: '100% !important',
                  minWidth: '100% !important',
                  display: 'block !important',
                },
              },
            }),
            // Hide the aggregation function name (e.g., "sum") from column headers
            '& .MuiDataGrid-columnHeaderTitleContainer .MuiDataGrid-columnHeaderTitleContainerContent > span:last-child':
              {
                display: 'none',
              },
            // Alternative selector for aggregation label
            '& .MuiDataGrid-aggregationColumnHeaderLabel': {
              display: 'none',
            },

            '& .MuiDataGrid-row .MuiDataGrid-cell': {
              alignItems: 'flex-start',
              py: 1,
            },
            '& .MuiDataGrid-row .holdings-name-cell': {
              whiteSpace: 'normal',
              overflow: 'visible',
              textOverflow: 'clip',
              py: 1,
            },
          }}
        />
      </GridContainer>

      <Box
        sx={{
          mt: 2,
          fontWeight: 600,
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant='subtitle1'>{t('total')}</Typography>
        <Typography variant='subtitle1'>
          {formatNumberToCurrency(total)}
        </Typography>
      </Box>

      <HoldingDetailsFlyout
        open={isFlyoutOpen}
        toggleDrawer={handleFlyoutClose}
        holding={selectedItem}
        endDate={endDate}
        advisorContext={advisorContext}
        showCusip={showCusip}
        investorId={investorId}
      />
      {accountFlyout}
    </Box>
  );
};

export default HoldingsTable;
